import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Phone, MapPin, ArrowUp } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Links bleiben strukturell identisch – Namen kommen jetzt aus i18n
  const footerLinks = {
    services: [
      { name: t('footer.links.services.0'), href: '#domaines' },
      { name: t('footer.links.services.1'), href: '#domaines' },
      { name: t('footer.links.services.2'), href: '#domaines' },
      { name: t('footer.links.services.3'), href: '#contact' }
    ],
    about: [
      { name: t('footer.links.about.0'), href: '#approche' },
      { name: t('footer.links.about.1'), href: '#apropos' },
      { name: t('footer.links.about.2'), href: '#temoignages' },
      { name: t('footer.links.about.3'), href: '#investissement' }
    ],
    legal: [
      { name: t('footer.links.legal.0'), href: '/impressum' },
      { name: t('footer.links.legal.1'), href: '/datenschutz' },
      { name: t('footer.links.legal.2'), href: '/agb' },
      { name: t('footer.links.legal.3'), href: '/widerruf' },
      { name: t('footer.links.legal.4'), href: '/cookies' },
      { name: t('footer.links.legal.5'), href: 'https://coachingfederation.org/ethics/code-of-ethics' }
    ]
  };

  return (
    <footer className="bg-charcoal text-cream-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-light text-cream-white tracking-wide mb-2">
                {t('footer.brand.name')}
              </h3>
              <p className="text-sm text-warm-gray font-medium tracking-widest">
                {t('footer.brand.byline')}
              </p>
            </div>
            <p className="text-warm-gray leading-relaxed mb-6">
              {t('footer.brand.tagline')}
            </p>
            <div className="flex items-center space-x-2 text-warm-sage">
              <Heart className="h-4 w-4" />
              <span className="text-sm">{t('footer.brand.badge')}</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-medium text-cream-white mb-6 tracking-wide">
              {t('footer.headings.services')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-warm-gray hover:text-warm-sage transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="text-lg font-medium text-cream-white mb-6 tracking-wide">
              {t('footer.headings.about')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.about.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-warm-gray hover:text-warm-sage transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Legal */}
          <div>
            <h4 className="text-lg font-medium text-cream-white mb-6 tracking-wide">
              {t('footer.headings.contact')}
            </h4>
            <div className="space-y-4 mb-6">
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-warm-sage flex-shrink-0" />
                <span className="text-warm-gray text-sm">{t('footer.contact.email')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-warm-sage flex-shrink-0" />
                <span className="text-warm-gray text-sm">{t('footer.contact.phone')}</span>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-warm-sage flex-shrink-0 mt-1" />
                <span className="text-warm-gray text-sm">{t('footer.contact.locations')}</span>
              </div>
            </div>

            <h5 className="text-sm font-medium text-cream-white mb-3">
              {t('footer.headings.legal')}
            </h5>
            <ul className="space-y-2">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('/') ? (
                    <Link
                      to={link.href}
                      className="text-warm-gray hover:text-warm-sage transition-colors duration-300 text-xs"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-warm-gray hover:text-warm-sage transition-colors duration-300 text-xs"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 pt-12 border-t border-warm-gray/20">
          <div className="text-center">
            <h3 className="text-2xl font-light text-cream-white mb-4 tracking-wide">
              {t('footer.cta.title')}
            </h3>
            <p className="text-warm-gray mb-8 max-w-2xl mx-auto">
              {t('footer.cta.text')}
            </p>
            <Button
              size="lg"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white font-medium px-8 py-4 rounded-md transition-all duration-300"
            >
              {t('footer.cta.button')}
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-warm-gray/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6 text-sm text-warm-gray">
              <span>{t('footer.bottom.copyright')}</span>
              <span>•</span>
              <span>{t('footer.bottom.icf')}</span>
              <span>•</span>
              <span>{t('footer.bottom.rights')}</span>
            </div>

            <button
              onClick={scrollToTop}
              className="p-3 bg-warm-sage hover:bg-warm-sage/90 text-cream-white rounded-full transition-all duration-300 group"
              aria-label={t('footer.bottom.backToTop')}
            >
              <ArrowUp className="h-4 w-4 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Final Message */}
      <div className="bg-warm-sage/10 border-t border-warm-sage/20">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p className="text-warm-sage italic text-lg font-light">
            {t('footer.finalMessage')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
